import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "image": "https://example.com/input_image.jpg",
 *   "prompt": "A beautiful sunset scene with gentle waves",
 *   "duration": "5",
 *   "mode": "std",
 *   "aspect_ratio": "16:9"
 * }
 * ```
 */
export interface KlingV21I2vParams {
  /**
   * 输入图像（必填），支持URL或Base64格式，URL会自动转换为Base64
   */
  image: string;
  /**
   * 视频生成提示词（可选），长度最大2500字符
   */
  prompt?: string;
  /**
   * 视频时长（必填），支持: "5"（5秒）或 "10"（10秒），也可以是整数 5 或 10
   */
  duration: string | any;
  /**
   * 生成模式（可选），标准(std)或专业(pro)，默认std
   * @default "std"
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 视频宽高比（可选），支持: 16:9（横屏）、9:16（竖屏）、1:1（方形）
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 控制摄像机运动的参数（可选）。如果未指定，模型将根据输入文本/图像智能匹配
   */
  camera_control?: {
    /**
     * 预定义摄像机运动类型
     * @values simple | down_back | forward_up | right_turn_forward | left_turn_forward
     */
    type?: 'simple' | 'down_back' | 'forward_up' | 'right_turn_forward' | 'left_turn_forward';
    /**
     * 摄像机配置参数。当type设置为simple时为必填，包含6个字段用于指定摄像机在不同方向上的运动或变化。要求6个参数中只能有一个非零，其余必须为零
     */
    config?: Record<string, any>;
  };
  /**
   * 尾帧图像（可选），支持URL或Base64格式
   */
  image_tail?: string;
  /**
   * 静态遮罩（可选）
   */
  static_mask?: string;
  /**
   * 动态遮罩列表（可选）
   */
  dynamic_masks?: Record<string, any>[];
}

export interface KlingV21I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v2_1_i2v
 *
 * Kling AI V2.1 图像生成视频接口，支持将静态图像转换为动态视频。
 * 
 * Model: kling-v2-1
 * Features:
 * - 支持图像到视频生成（I2V）
 * - 支持5秒或10秒视频时长
 * - 支持三种宽高比：16:9（横屏）、9:16（竖屏）、1:1（方形）
 * - 支持标准模式（std）和专业模式（pro）
 * - 支持预定义摄像机运动控制（simple、down_back、forward_up、right_turn_forward、left_turn_forward）
 * - 支持尾帧图像、静态遮罩和动态遮罩
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV21I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV21I2v(
 *   {
 *     "image": "https://example.com/input_image.jpg",
 *     "prompt": "A beautiful sunset scene with gentle waves",
 *     "duration": "5",
 *     "mode": "std",
 *     "aspect_ratio": "16:9"
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV21I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingV21I2v(
  params: KlingV21I2vParams,
): Promise<KlingV21I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v2_1_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV21I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
